import { Check } from '@mui/icons-material';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import ExpandContent from '../../custom-icons/ExpandContent';
import { useUser } from '../../hooks/useUser';
import { useUpdateUserDetailsMutation } from '../../services/projects/userDetailsApi';
import versionInfo from './versions/latest.json';
import text from './versions/latest.md?raw';

const { version } = versionInfo[0];

const UserLicenseDialog = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { userDetails, isLoadingDetails } = useUser();
  const [updateUserDetails, { isLoading }] = useUpdateUserDetailsMutation();
  const { shadows, palette } = useTheme();

  useEffect(() => {
    if (!isLoadingDetails && userDetails) {
      if (userDetails.accepted_terms_version !== version) {
        setOpen(true);
      }
    }
  }, [userDetails, isLoadingDetails]);

  const handleAccept = async () => {
    try {
      const { version } = versionInfo[0];
      await updateUserDetails({
        accepted_terms_version: version,
      }).unwrap();
      setOpen(false);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Failed to update terms acceptance. Please try again later.', {
        variant: 'error',
      });
    }
  };
  return (
    <Dialog
      open={open}
      aria-labelledby="user-license-dialog-title"
      fullWidth
      maxWidth={expanded ? 'md' : 'xs'}
    >
      <DialogTitle
        id="user-license-dialog-title"
        variant="h3"
        sx={{ boxShadow: shadows[palette.mode == 'dark' ? 25 : 32], zIndex: 2 }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ pr: '10px' }}>
            Updated Terms of Service
            <div>
              <Typography
                variant="body3 compact"
                sx={{ display: 'inline-block', mt: '8px' }}
                color="textSecondary"
              >
                You must agree to these terms in order to use Stryten Energy inCOMMAND.{' '}
              </Typography>
            </div>
          </Box>
          <IconButton
            sx={{
              padding: '8px',
              borderRadius: '2px',
              border: '1px solid',
              borderColor: 'text.tertiary',
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <ExpandContent
              sx={{ height: '24px', width: '24px', padding: '4px', color: 'text.secondary' }}
            />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: 'background.default',
          maxHeight: !expanded ? 212 : undefined,
          display: 'flex',
          flexFlow: 'column',
          padding: '16px',
          paddingTop: '16px!important',
        }}
      >
        <Box
          sx={{
            color: 'text.secondary',
            fontSize: '14px',
            width: '100%',
            overflow: 'auto',
            paddingRight: '16px',
            flex: 1,
          }}
        >
          <Markdown>{text}</Markdown>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexWrap: 'wrap',
          padding: '16px 16px 24px 16px',
          boxShadow: shadows[palette.mode == 'dark' ? 25 : 32],
        }}
      >
        <Box width={'100%'} display="flex" justifyContent="center" gap={1}>
          <FormControlLabel
            slotProps={{ typography: { variant: 'body3', color: 'textSecondary' } }}
            value={acceptedTerms}
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
              />
            }
            label="I have read and agree to the terms and conditions."
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Button
            sx={{ flex: 1 }}
            size="large"
            variant="contained"
            color="primary"
            onClick={handleAccept}
            disabled={!acceptedTerms}
            loading={isLoading}
          >
            <Check sx={{ height: 20 }} />
            Accept
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserLicenseDialog;
